// @wc-ignore-file
// CR-147: pure client-side rules for uploading a document to a CR. Every string
// in here is DATA (MIME types, a bucket name, error codes), never UI copy — the
// file is excluded from wuchale extraction so a non-English locale cannot
// silently translate a MIME type and break the comparisons below.

import { CR_UPLOAD_ACCEPTED_MIME_TYPES, SVG_MIME_TYPE } from "@shared/mime-types";

/** Private bucket created by 20260922120100_cr147_uploads.sql. */
export const CR_UPLOAD_BUCKET = "cr-uploads";

/**
 * Mirrors the bucket's `file_size_limit` (50 MiB). Checked client-side purely
 * so the user gets the product message instead of a raw Storage 413; Storage
 * and the RPC remain the real boundary.
 */
export const CR_UPLOAD_MAX_SIZE_BYTES = 50 * 1024 * 1024;

/**
 * Extensions the OS often has no MIME mapping for, so `File.type` comes back
 * empty — Markdown on Windows above all, which is exactly the kind of file this
 * feature exists for. Without a fallback the client would refuse a `.md` with
 * "unsupported type" on one platform and accept it on another.
 *
 * `.svg` is in the list for the same reason: some Windows installs report no
 * type for it (CR-173).
 *
 * Only extensions whose type is unambiguous are listed; anything else keeps the
 * browser's own label. The inferred type is what gets stored as the object's
 * Content-Type, so the bucket allow-list and the RPC still see a real MIME type.
 */
const EXTENSION_MIME_FALLBACK: Readonly<Record<string, string>> = {
  md: "text/markdown",
  markdown: "text/markdown",
  txt: "text/plain",
  csv: "text/csv",
  tsv: "text/tsv",
  json: "application/json",
  svg: SVG_MIME_TYPE,
};

/**
 * The `accept` attribute for the file picker. Built from the SAME set the
 * bucket's `allowed_mime_types` holds — deliberately not the knowledge base's
 * `KB_ACCEPTED_MIME_TYPES`, whose `image/*` wildcard would offer image types the
 * bucket refuses. SVG IS offered (CR-173): the parse route sanitizes and rewrites
 * it, and the storage policy hides it from other members until that is done.
 *
 * The extensions are appended for the same reason the fallback map exists: a
 * dialog filtering on MIME types alone hides `.md` on the platforms that have
 * no mapping for it.
 */
export const CR_UPLOAD_ACCEPT_ATTRIBUTE = [
  ...CR_UPLOAD_ACCEPTED_MIME_TYPES,
  ...Object.keys(EXTENSION_MIME_FALLBACK).map((extension) => `.${extension}`),
].join(",");

/**
 * The Content-Type to store the file under: the browser's own label when it has
 * one, else the extension fallback. Used for BOTH the client check and the
 * Storage upload, so the two can never disagree.
 */
export function resolveCrUploadMimeType(file: File): string {
  if (file.type) return file.type;
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_MIME_FALLBACK[extension] ?? "";
}

/**
 * The file's type is outside the CR-upload allow-list. A class rather than a
 * message so the page can map it to product copy: under vitest wuchale resolves
 * every user-facing string to `""`, so a test can only assert on the type.
 */
export class UnsupportedFileError extends Error {
  constructor(mimeType: string) {
    super(`unsupported_file_type:${mimeType || "unknown"}`);
    this.name = "UnsupportedFileError";
  }
}

/** The file is larger than the bucket allows. Same reasoning as above. */
export class FileTooLargeError extends Error {
  constructor(sizeBytes: number) {
    super(`file_too_large:${sizeBytes}`);
    this.name = "FileTooLargeError";
  }
}

/**
 * Whether the file may be uploaded at all. A type that survives neither the
 * browser nor the fallback map is refused here rather than by the RPC, which
 * would answer with its constant 42501 and no product message.
 */
export function isCrUploadSupported(file: File): boolean {
  return CR_UPLOAD_ACCEPTED_MIME_TYPES.has(resolveCrUploadMimeType(file));
}

/**
 * Why the file cannot be uploaded to a CR, or `null` when it can — the one
 * client-side rule behind the CR page's upload and the Feedback dialog's
 * picker (CR-296), so the two can never disagree. Storage and the RPC remain
 * the real boundary.
 */
export function checkCrUploadFile(file: File): UnsupportedFileError | FileTooLargeError | null {
  if (!isCrUploadSupported(file)) return new UnsupportedFileError(resolveCrUploadMimeType(file));
  if (file.size > CR_UPLOAD_MAX_SIZE_BYTES) return new FileTooLargeError(file.size);
  return null;
}

/** Why a picked file was not queued (CR uploads and suggestion attachments alike). */
export type UploadFileRefusalCode = "unsupported" | "too_large" | "too_many_files";

/** A pick split into the files to queue and the ones refused, in pick order. */
export type UploadFilePick = {
  accepted: File[];
  refused: { name: string; code: UploadFileRefusalCode }[];
};

/**
 * Split a pick by `check` (type and size) and a queue cap: `alreadyCount` is
 * what the queue already holds; past `maxFiles` the rest is refused. The one
 * loop behind `pickCrUploadFiles` and CR-235's `pickSuggestionFiles`.
 */
export function pickUploadFiles(
  picked: readonly File[],
  alreadyCount: number,
  maxFiles: number,
  check: (file: File) => Error | null,
): UploadFilePick {
  const accepted: File[] = [];
  const refused: UploadFilePick["refused"] = [];
  for (const file of picked) {
    const refusal = check(file);
    if (refusal) {
      refused.push({
        name: file.name,
        code: refusal instanceof FileTooLargeError ? "too_large" : "unsupported",
      });
    } else if (alreadyCount + accepted.length >= maxFiles) {
      refused.push({ name: file.name, code: "too_many_files" });
    } else {
      accepted.push(file);
    }
  }
  return { accepted, refused };
}

/**
 * CR-296 S2.1: `pickUploadFiles` with CR-147's rules. CR uploads have no
 * server-side count cap, so `maxFiles` is the caller's UX guard (the Feedback
 * dialog passes the suggestion cap, Q2). A parameter rather than an import:
 * cr-suggestion-uploads.ts already imports this module.
 */
export function pickCrUploadFiles(
  picked: readonly File[],
  alreadyCount: number,
  maxFiles: number,
): UploadFilePick {
  return pickUploadFiles(picked, alreadyCount, maxFiles, checkCrUploadFile);
}

/** CR-173: the upload is an SVG, which the parse route sanitizes and rewrites. */
export function isCrUploadSvg(mimeType: string): boolean {
  return mimeType === SVG_MIME_TYPE;
}

/** Structural shape so this stays free of the API layer's types (no import cycle). */
type OpenableUpload = { mime_type: string; parsing_status: string };

/**
 * Whether the row may offer the file's bytes. Nothing is offered while it is
 * still being parsed (CR-147). CR-173: an SVG is offered only once `done` —
 * before that the stored bytes are the uploader's original, which may carry
 * script, and a failed SVG never becomes safe. The storage policy already
 * refuses those bytes to everyone but the uploader (admins included); this
 * keeps the affordance off for the uploader too. Other types keep their CR-147
 * behaviour: a failed PDF is still downloadable.
 */
export function isCrUploadOpenable(upload: OpenableUpload): boolean {
  if (isCrUploadSvg(upload.mime_type)) {
    return upload.parsing_status === "done";
  }
  return !isCrUploadParsing(upload.parsing_status);
}

/**
 * `{prefixId}/{uuid}-{sanitized name}` — the exact shape the storage INSERT
 * policy (`cr_upload_link_state`) and both linking RPCs parse. The prefix is the
 * CR id for a CR upload (`create_cr_uploaded_document`, CR-147) or the project
 * id for project documentation (`create_project_uploaded_document`, CR-181).
 * The uuid keeps two uploads of the same file apart; the RPC recovers the
 * display name by stripping everything up to the first `-` after it.
 */
export function buildUploadStoragePath(prefixId: string, sanitizedFileName: string): string {
  return `${prefixId}/${crypto.randomUUID()}-${sanitizedFileName}`;
}

/**
 * Which product message an upload failure deserves. Kept here, next to the
 * error classes, so the page renders copy and nothing else — and so a test can
 * assert the branch without reading a string (wuchale resolves them to `""`
 * under vitest).
 */
export function crUploadErrorReason(
  error: unknown,
): "unsupported" | "too-large" | "unknown" | null {
  if (!error) return null;
  if (error instanceof UnsupportedFileError) return "unsupported";
  if (error instanceof FileTooLargeError) return "too-large";
  return "unknown";
}

/** How often the linked list is refreshed while a file is being parsed. */
export const CR_UPLOAD_POLL_INTERVAL_MS = 3_000;

/**
 * How long after an upload the list keeps polling. Parsing normally finishes in
 * seconds; a row still pending after five minutes is a stuck parse (Stage 2 has
 * no sweeper yet), and polling it forever would keep every open CR page issuing
 * a request every three seconds until the tab is closed.
 */
export const CR_UPLOAD_POLL_WINDOW_MS = 5 * 60 * 1_000;

/** The two statuses that still expect to change on their own. */
export function isCrUploadParsing(status: string): boolean {
  return status === "pending" || status === "processing";
}

/** Structural shape so this stays free of the API layer's types (no import cycle). */
type PollingRow = {
  created_at: string;
  upload: { parsing_status: string } | null;
};

/**
 * `true` while THIS row is still being parsed and young enough to be worth
 * watching. Measured from `created_at` rather than from a "last upload"
 * timestamp in React state so a page reload (or a second viewer) sees the same
 * window, and a long-open page cannot resurrect polling for a row that has been
 * stuck since yesterday. A `created_at` that Date.parse rejects yields NaN, which fails the
 * comparison — the safe side, since it stops polling rather than starting it.
 */
export function isCrUploadInPollWindow(row: PollingRow, now: number = Date.now()): boolean {
  if (!row.upload || !isCrUploadParsing(row.upload.parsing_status)) return false;
  return now - Date.parse(row.created_at) < CR_UPLOAD_POLL_WINDOW_MS;
}

/** `true` while any row in the list is worth watching. */
export function hasCrUploadInProgress(
  rows: readonly PollingRow[] | undefined,
  now: number = Date.now(),
): boolean {
  return (rows ?? []).some((row) => isCrUploadInPollWindow(row, now));
}

/**
 * `refetchInterval` for the CR page's linked-documents query: poll only while
 * something is actually being prepared.
 */
export function crLinkedDocsRefetchInterval(
  rows: readonly PollingRow[] | undefined,
  now: number = Date.now(),
): number | false {
  return hasCrUploadInProgress(rows, now) ? CR_UPLOAD_POLL_INTERVAL_MS : false;
}

/** Structural shape so this stays free of the API layer's types (no import cycle). */
type RemovableRow = { created_by: string | null };

/**
 * Who may remove an uploaded file, mirroring delete_cr_uploaded_document: its
 * uploader (containers.created_by), or an owner/admin of the CR's project.
 *
 * The RPC is the boundary — this only decides whether the affordance is shown,
 * so a member never clicks a button that can only fail. An unknown viewer never
 * matches an unknown uploader: both sides must be a real id.
 */
export function canRemoveCrUpload(
  row: RemovableRow,
  viewerId: string | null | undefined,
  isOwnerOrAdmin: boolean,
): boolean {
  if (isOwnerOrAdmin) return true;
  return !!viewerId && !!row.created_by && row.created_by === viewerId;
}
